import { BehaviorSubject, finalize, Observable, Subject, takeUntil, tap } from "rxjs";
import { FeatureFlagService } from "../services/feature-flag.service";
import { DataSource } from "@angular/cdk/collections";
import { FeatureFlag } from "../models/feature-flag.model";
import { HttpErrorResponse } from "@angular/common/http";
import { ToastService } from "../services/toast.service";

/**
 * PURPOSE: configures the datasource for the features flags table in the feature flags component
 */
export class FeatureFlagsDataSource implements DataSource<FeatureFlag> {
  private readonly TOAST_DURATION: number = 7000;
  private onDestroy$: Subject<boolean> = new Subject();
  private featureFlagsSubject: BehaviorSubject<FeatureFlag[]> = new BehaviorSubject<FeatureFlag[]>(
    []
  );
  public isLoading: boolean = false;
  public loadingMap: { [id: number]: boolean } = {};

  constructor(
    private featureFlagService: FeatureFlagService,
    private toastService: ToastService
  ) {}

  connect(): Observable<FeatureFlag[]> {
    return this.featureFlagsSubject.asObservable();
  }

  get data(): FeatureFlag[] {
    return this.featureFlagsSubject.value;
  }

  public loadFeatureFlags(): void {
    this.isLoading = true;
    this.featureFlagService
      .getFeatureFlags()
      .pipe(
        tap((): boolean => (this.isLoading = true)),
        takeUntil(this.onDestroy$),
        finalize((): boolean => (this.isLoading = false))
      )
      .subscribe({
        next: (featureFlags: FeatureFlag[]): void => {
          this.featureFlagsSubject.next(featureFlags);
        },
        error: (error: HttpErrorResponse): void => {
          console.error("Error loading feature flags", error);
          this.featureFlagsSubject.next([]);
        }
      });
  }

  public updateFeatureFlag(featureFlag: FeatureFlag): void {
    const featureFlagId: number | undefined = featureFlag.id;
    if (featureFlagId) {
      this.loadingMap[featureFlagId] = true;

      this.featureFlagService
        .updateFeatureFlag(featureFlag)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize((): boolean => (this.loadingMap[featureFlagId] = false))
        )
        .subscribe({
          next: (updatedFlag: FeatureFlag): void => {
            const currentFlags: FeatureFlag[] = this.featureFlagsSubject.value;
            const index: number = currentFlags.findIndex(
              (flag: FeatureFlag): boolean => flag.id === updatedFlag.id
            );

            if (index !== -1) {
              const updatedFlags: FeatureFlag[] = [...currentFlags];
              updatedFlags[index] = updatedFlag;
              this.featureFlagsSubject.next(updatedFlags);
            }
          },
          error: (error: HttpErrorResponse): void => {
            this.toastService.open(
              `Failed to update feature flag [${featureFlag.name}]: [${error.error.error}]`,
              "Close",
              {
                duration: this.TOAST_DURATION,
                panelClass: ["bg-red-600"]
              }
            );
          },
          complete: (): void => {
            this.toastService.open(
              `Feature flag [${featureFlag.name}] updated successfully.`,
              "Close",
              {
                duration: this.TOAST_DURATION,
                panelClass: ["bg-green-600"]
              }
            );
          }
        });
    }
  }

  public addFeatureFlag(featureFlag: FeatureFlag): void {
    this.featureFlagService
      .createFeatureFlag(featureFlag)
      .pipe(
        tap((): boolean => (this.isLoading = true)),
        takeUntil(this.onDestroy$),
        finalize((): boolean => (this.isLoading = false))
      )
      .subscribe({
        next: (newFlag: FeatureFlag): void => {
          const currentFlags: FeatureFlag[] = this.featureFlagsSubject.value;
          this.featureFlagsSubject.next([...currentFlags, newFlag]);
        },
        error: (error: HttpErrorResponse): void => {
          console.log({ error });
          this.toastService.open(
            `Failed to create feature flag [${featureFlag.name}]: [${error.error.error}]`,
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
        },
        complete: (): void => {
          this.toastService.open(
            `Feature flag [${featureFlag.name}] created successfully.`,
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-green-600"]
            }
          );
        }
      });
  }

  disconnect(): void {
    this.featureFlagsSubject.complete();
  }
}
